import { useState, useEffect } from "react";
import axios from "axios";
import { Calendar, Mail, Phone, Target, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLeadsPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = () => {
    setLoading(true);
    axios.get(`${API}/leads.php`).then((res) => {
      setLeads(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit"
    });
  };

  const goalLabels = {
    wealth: "Grow Wealth",
    insurance: "Protect Family",
    tax: "Save Tax",
    other: "Other",
  };

  return (
    <div data-testid="admin-leads-page" className="pt-20 min-h-screen bg-[#F9FAFB]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-['Outfit'] text-2xl md:text-3xl font-semibold text-[#0B1F3A]">
              Lead Management
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {leads.length} total leads captured
            </p>
          </div>
          <button
            onClick={fetchLeads}
            className="bg-[#3B82F6] text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-[#2563EB] transition-all"
            data-testid="admin-refresh-btn"
          >
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#3B82F6] animate-spin" />
          </div>
        ) : leads.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
            <p className="text-gray-500">No leads captured yet.</p>
          </div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="md:hidden space-y-4">
              {leads.map((lead, i) => (
                <div
                  key={lead.id}
                  className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm"
                  data-testid={`lead-card-${i}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-['Outfit'] font-semibold text-[#0B1F3A]">{lead.name}</span>
                    {lead.goal && (
                      <span className="text-xs bg-[#3B82F6]/5 text-[#3B82F6] px-2.5 py-1 rounded-full font-medium">
                        {goalLabels[lead.goal] || lead.goal}
                      </span>
                    )}
                  </div>
                  <div className="space-y-1.5 text-sm text-gray-600">
                    <p className="flex items-center gap-2">
                      <Phone className="w-3.5 h-3.5 text-gray-400" /> {lead.phone}
                    </p>
                    <p className="flex items-center gap-2">
                      <Mail className="w-3.5 h-3.5 text-gray-400" /> {lead.email}
                    </p>
                    <p className="flex items-center gap-2">
                      <Calendar className="w-3.5 h-3.5 text-gray-400" /> {formatDate(lead.created_at)}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
              <table className="w-full" data-testid="admin-leads-table">
                <thead>
                  <tr className="bg-[#F9FAFB] border-b border-gray-100">
                    <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Phone</th>
                    <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Goal</th>
                    <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead, i) => (
                    <tr
                      key={lead.id}
                      className="border-b border-gray-50 hover:bg-[#F9FAFB] transition-colors"
                      data-testid={`lead-row-${i}`}
                    >
                      <td className="px-6 py-4">
                        <span className="font-medium text-[#0B1F3A] text-sm">{lead.name}</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{lead.phone}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{lead.email}</td>
                      <td className="px-6 py-4">
                        {lead.goal ? (
                          <span className="text-xs bg-[#3B82F6]/5 text-[#3B82F6] px-2.5 py-1 rounded-full font-medium">
                            {goalLabels[lead.goal] || lead.goal}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">{formatDate(lead.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
